/* eslint-disable max-lines */
"use client";

import React, { useCallback, useRef, useState } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import ScrollReveal, {
  expandTravelDistance,
  HEADER_HEIGHT,
  ScrollRevealContent,
} from "@/components/common/ScienceScrollReveal/ScienceScrollReveal";
import ScienceSectionIntro from "@/components/common/ScienceSectionIntro/ScienceSectionIntro";
import ScienceSlider, {
  ScienceIndicator,
  ScienceSlideContent,
  type ScienceSliderApi,
} from "@/components/common/ScienceSlider/ScienceSlider";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import type {
  ScienceResponsiveImage,
  ScienceRootCausesData,
} from "@/data/hair/brand/learn/science/science.types";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

const TRANSITION_SPEED = 500;
const CIRCLE_SIZE_MOBILE = 45;
const CIRCLE_SIZE_DESKTOP = 20;

function ResponsiveImage({
  src,
  alt,
  className,
  loading = "lazy",
}: {
  src?: ScienceResponsiveImage;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}) {
  if (!src?.mobile && !src?.desktop) return null;
  const fallback = src.mobile || src.desktop || "";
  return (
    <div className="h-full">
      <Picture
        src={fallback}
        sources={
          src.mobile && src.desktop ? [{ media: "(min-width: 768px)", srcSet: src.desktop }] : []
        }
        alt={alt}
        loading={loading}
        className={className}
      />
    </div>
  );
}

const getDisplacementMap = (size: number) => [
  { x: -size, y: -(size * 2) },
  { x: size, y: -size * 2 },
  { x: -size, y: 0 },
  { x: size, y: 0 },
  { x: -size, y: size * 2 },
  { x: size, y: size * 2 },
];

function Circle({
  coordinates,
  settings,
  size,
}: {
  coordinates: { x: number; y: number };
  settings: Record<string, unknown>;
  size: number;
}) {
  const circleRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!circleRef.current) return;
    gsap.fromTo(
      circleRef.current,
      {
        xPercent: coordinates.x - 50,
        yPercent: coordinates.y - 50,
      },
      {
        xPercent: -50,
        yPercent: -50,
        scrollTrigger: {
          ...settings,
          scrub: 1.5,
        },
      }
    );
  });

  return (
    <div
      ref={circleRef}
      className="absolute top-1/2 left-1/2 aspect-square rounded-full border border-white/50"
      style={{ width: `${size}%` }}
    />
  );
}

function CircleAnimation({
  settings,
  size = 45,
  show,
  className,
  ...rest
}: {
  settings: Record<string, unknown>;
  size?: number;
  show?: boolean;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLg = !useIsMobile(768);

  useGSAP(() => {
    if (!containerRef.current) return;

    const targetWidth = window.innerWidth - 64;
    const targetHeight = (targetWidth * 16) / 11.5;

    const fromVars = isLg
      ? { paddingBottom: 0 }
      : {
          width: targetWidth,
          left: 32,
          height: targetHeight,
          paddingBottom: 0,
        };

    const toVars = isLg
      ? {
          paddingBottom: 140,
          scrollTrigger: { ...settings, scrub: 2 },
        }
      : {
          left: 0,
          width: "100vw",
          height: "100%",
          paddingBottom: 155,
          scrollTrigger: { ...settings, scrub: 2 },
        };

    gsap.fromTo(containerRef.current, fromVars, toVars);
  }, [isLg]);

  useGSAP(() => {
    if (!containerRef.current) return;
    gsap.to(containerRef.current, {
      opacity: show ? 1 : 0,
      duration: 0.1,
    });
  }, [show]);

  return (
    <div
      ref={containerRef}
      data-intro
      className={cn("absolute top-0 left-0 h-full w-full", className)}
      {...rest}
    >
      <div className="relative w-full h-full">
        {getDisplacementMap(size).map((coordinates, index) => (
          <Circle settings={settings} key={index} coordinates={coordinates} size={size} />
        ))}
      </div>
    </div>
  );
}

function Halo({ delay }: { delay: number }) {
  const haloRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!haloRef.current) return;
    gsap.to(haloRef.current, {
      scale: 0.75,
      duration: 5,
      repeat: -1,
      delay,
    });
    gsap.to(haloRef.current, {
      startAt: { opacity: 1 },
      opacity: 0,
      duration: 5,
      repeat: -1,
      delay,
    });
  });

  return (
    <div
      ref={haloRef}
      className="absolute inset-0 rounded-full border border-white/50 aspect-square opacity-0"
    />
  );
}

function CircleSlide({
  children,
  haloNumber = 5,
  size = 45,
  active,
  delay = 0,
  onClick,
  className,
}: {
  children: React.ReactNode;
  haloNumber?: number;
  size?: number;
  active?: boolean;
  delay?: number;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <div
      data-active={active}
      onClick={onClick}
      className={cn(
        "relative flex-shrink-0 aspect-square flex items-center justify-center rounded-full border border-white/50 text-white transition-[border-color] duration-200",
        active && "border-white",
        className
      )}
      style={{ width: `${size}%` }}
      role={onClick ? "button" : undefined}
    >
      <span
        data-content
        className={cn(
          "transition-opacity duration-200 ease-in-out opacity-0 md:opacity-40",
          active && "opacity-100"
        )}
        style={{ transitionDelay: active ? `${delay + 100}ms` : "0ms" }}
      >
        {children}
      </span>
      <div
        data-halo
        className={cn(
          "absolute inset-0 transition-opacity duration-400",
          active ? "opacity-100" : "opacity-0"
        )}
        style={{ transitionDelay: active ? `${delay + 100}ms` : "0ms" }}
      >
        {Array(haloNumber)
          .fill(0)
          .map((_, i) => (
            <Halo key={i} delay={i * 1} />
          ))}
      </div>
    </div>
  );
}

function RootCausesMobile({
  causes,
  backgroundSequence,
  backgroundScaleInitial = 1.8,
  backgroundScale = 2.3,
  activeCause,
  setActiveCause,
}: ScienceRootCausesData & {
  activeCause: number;
  setActiveCause: (index: number) => void;
}) {
  const activeSlideRef = useRef(0);

  const handler = useCallback(
    (idx: number, dir: string) => {
      segmentEvent("Carousel Clicked", {
        arrow: dir,
        module_location: "mid_fold",
        module_name: "Root Causes",
        slide_number: idx + 1,
        slide_name: causes[idx]?.name,
      });
    },
    [causes]
  );

  return (
    <ScrollReveal
      selector="root-causes-mobile"
      backgroundSequence={backgroundSequence}
      backgroundScaleInitial={backgroundScaleInitial}
      backgroundScale={backgroundScale}
      showContentTreshold={0.5}
      offset={-100}
      backgroundColor="#000"
      animationConfig={{
        imageScale: true,
        backgroundColor: true,
        blur: true,
        clipPath: false,
        overlay: true,
      }}
      content={({ intro }) => (
        <ScrollRevealContent
          intro={intro}
          className="relative h-full w-full min-h-0 p-0 flex flex-col items-center justify-between text-center text-master-base-default"
        >
          <CircleAnimation
            data-intro-element
            show={intro}
            size={CIRCLE_SIZE_MOBILE}
            className="pointer-events-none"
            settings={{
              start: `-=${HEADER_HEIGHT + expandTravelDistance + 50}`,
              end: `+=${expandTravelDistance}`,
              scrub: 2,
              trigger: `[data-scroll-reveal='root-causes-mobile']`,
            }}
          />

          {/*
           * Ingredients pattern: tall Embla track fills the module so the whole area
           * is swipeable; text/dots sit above with pointer-events-none.
           * Do NOT use -top on the Embla container (overflow:hidden clips halo tops).
           * Optical lift = extra bottom padding on the centered row (Gatsby list top:-30px).
           */}
          <div className="absolute inset-0 z-[3]" data-intro-element>
            <ScienceSlider
              loop
              align="center"
              showArrows
              // Gatsby: autoWidth + CircleSlide width 45% + gap 64
              slideStyle={{ flex: `0 0 ${CIRCLE_SIZE_MOBILE}%`, marginRight: 64 }}
              onMove={(index) => {
                activeSlideRef.current = index;
                setActiveCause(index);
              }}
              onClickSlide={(index) => {
                const dir = index > activeSlideRef.current ? "right" : "left";
                handler(index, dir);
              }}
              className="h-full w-full [&_.science-slider-arrow]:-translate-y-[calc(50%+55px)]"
              viewportClassName="h-full w-full"
              containerClassName="h-full w-full items-center pb-[60px]"
            >
              {causes.map((cause, index) => (
                <CircleSlide
                  key={index}
                  size={100}
                  active={activeCause === index}
                  className="w-full"
                >
                  {cause.name}
                </CircleSlide>
              ))}
            </ScienceSlider>
          </div>

          <div className="absolute bottom-0 w-full flex flex-col gap-4 items-center px-6 pb-8 z-[2] pointer-events-none">
            <ScienceSlideContent
              className="w-full h-[100px] lg:h-[120px]"
              active={activeCause}
              speed={TRANSITION_SPEED}
              contents={causes.map((cause, i) => (
                <div className="flex items-center" key={`cause-${i}`}>
                  <TextElementNova
                    element="p"
                    className="Body-20-Dia-Normal text-nova-base-white"
                    text={cause.text}
                  />
                </div>
              ))}
            />
            <ScienceIndicator
              speed={TRANSITION_SPEED}
              length={causes.length}
              active={activeCause}
            />
          </div>
        </ScrollRevealContent>
      )}
    />
  );
}

function RootCausesDesktop({
  causes,
  image,
  activeCause,
  setActiveCause,
}: ScienceRootCausesData & {
  activeCause: number;
  setActiveCause: (index: number) => void;
}) {
  const sliderRef = useRef<ScienceSliderApi>(null);

  const setCause = (index: number) => {
    sliderRef.current?.go(index);
    setActiveCause(index);
  };

  const onClick = (index: number) => {
    if (typeof document !== "undefined" && document.body.clientWidth < 768) {
      return;
    }
    setCause(index);
    segmentEvent("Module Tab Clicked", {
      text: causes[index]?.name,
    });
  };

  const isActive = (active: number, index: number) => {
    if (Math.abs(active - index) === causes?.length) return true;
    return active === index;
  };

  return (
    <ScrollReveal
      className="relative w-full overflow-hidden h-full"
      selector="root-causes-desktop"
      background={
        <ResponsiveImage src={image?.src} alt={image?.alt || ""} className="object-cover" />
      }
      showContentTreshold={0.9}
      offset={200}
      backgroundColor="#000"
      animationConfig={{
        imageScale: true,
        backgroundColor: true,
        blur: true,
        clipPath: false,
        overlay: true,
      }}
      content={({ intro }) => (
        <ScrollRevealContent
          intro={intro}
          className="relative h-full w-full flex flex-col gap-y-[60px] items-center justify-between pt-[200px] pb-10 text-center text-master-base-default"
        >
          <CircleAnimation
            data-intro-element
            show={intro}
            size={CIRCLE_SIZE_DESKTOP}
            settings={{
              start: `-=${HEADER_HEIGHT + expandTravelDistance + 234}`,
              end: `+=${expandTravelDistance}`,
              scrub: 2,
              trigger: `[data-scroll-reveal='root-causes-desktop']`,
            }}
          />

          <div
            className={cn(
              "w-full my-auto pb-20 -translate-y-[50px]",
              intro && "pointer-events-none"
            )}
            data-intro-element
          >
            <ScienceSlider
              ref={sliderRef}
              loop
              align="center"
              showArrows
              // Gatsby: autoWidth + CircleSlide width 20% + gap 64
              slideStyle={{ flex: `0 0 ${CIRCLE_SIZE_DESKTOP}%`, marginRight: 64 }}
              onMove={(index) => setCause(index)}
              onClickSlide={onClick}
            >
              {causes.map((cause, index) => (
                <CircleSlide
                  key={index}
                  size={100}
                  active={isActive(activeCause, index)}
                  className="w-full"
                >
                  <TextElementNova
                    text={cause.name}
                    className="Title-10-Super-Normal text-nova-base-white"
                  />
                </CircleSlide>
              ))}
            </ScienceSlider>
          </div>

          <div className="w-full flex flex-col gap-4 items-center px-6 pt-0">
            <ScienceSlideContent
              className="w-full min-h-[67px] max-w-[620px]"
              active={activeCause}
              contents={causes.map((cause, i) => (
                <div className="flex items-center" key={`cause-${i}`}>
                  <TextElementNova
                    element="p"
                    className="Body-20-Dia-Normal text-nova-base-white"
                    text={cause.text}
                  />
                </div>
              ))}
            />
          </div>
        </ScrollRevealContent>
      )}
    />
  );
}

export default function ScienceRootCauses(props: ScienceRootCausesData) {
  const { intro, id } = props;
  const [activeCause, setActiveCause] = useState(0);

  const commonProps = {
    ...props,
    activeCause,
    setActiveCause,
  };

  return (
    <section
      data-section
      id={id}
      className={cn(
        "relative z-[1] py-8 md:py-12 xl:py-20 bg-nova-base-solid-20",
        id === "root-causes" && "max-lg:pb-0"
      )}
    >
      <div className="w-full max-w-[1440px] mx-auto md:px-8">
        {intro && <ScienceSectionIntro {...intro} />}
        <div className="md:hidden">
          <RootCausesMobile {...commonProps} />
        </div>
        <div className="hidden md:block">
          <RootCausesDesktop {...commonProps} />
        </div>
      </div>
    </section>
  );
}
